import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { apiFetch } from '@/lib/api';

const ERRORS: Record<string, string> = {
  sso_unavailable: "La connexion SSO n'est pas configurée sur cet environnement.",
  sso_refused: 'La connexion a été refusée par Microsoft Entra ID.',
  invalid_flow: 'La tentative de connexion a expiré. Recommencez.',
  invalid_claims: "Votre compte Entra ne transmet pas les informations nécessaires. Contactez le support informatique.",
  provider_error: 'La connexion a échoué. Recommencez ou contactez le support informatique.',
};

/**
 * Screen 00 (00-connexion.html): presentation on the left, a single
 * « Connexion SSO » button on the right. No email, no password (ADR-002).
 * The button is a full-page navigation to the BFF, which runs the OIDC flow.
 */
export function LoginPage() {
  const [params] = useSearchParams();
  const error = params.get('erreur');
  const returnTo = params.get('returnTo') ?? '/';
  const providers = useQuery({
    queryKey: ['auth-providers'],
    queryFn: () => apiFetch<{ sso: boolean; dev: boolean }>('/api/auth/providers'),
  });

  return (
    <div className="flex min-h-screen flex-wrap bg-card text-foreground">
      <section aria-label="Présentation" className="relative box-border flex min-w-0 flex-[1_1_480px] flex-col justify-between gap-12 overflow-hidden bg-card py-14 pl-[78px] pr-16">
        <div aria-hidden="true" className="absolute inset-y-0 left-0 w-3.5 bg-brand-gradient-vertical" />
        <div aria-hidden="true" className="absolute bottom-10 right-10 h-28 w-[182px] bg-dots-ornament opacity-55" />
        <img src="/product-portal-logo.png" alt="Product Portal — Europa Group" className="block h-auto w-60" />
        <div className="relative flex max-w-[520px] flex-col gap-7">
          <span className="text-sm font-bold uppercase tracking-[0.12em] text-foreground-secondary">Direction Produit &amp; Digital</span>
          <h1 className="text-brand-gradient m-0 text-[46px] font-black leading-[1.05] tracking-[-0.005em]">
            Un seul endroit pour vos demandes à l'équipe produit.
          </h1>
          <p className="m-0 text-[17px] leading-[1.55] text-foreground-secondary">
            Signalez un bug, proposez une évolution, demandez un accès. Suivez l'avancement, échangez avec l'équipe et retrouvez les release
            notes des produits.
          </p>
          <ul className="m-0 flex list-none flex-col p-0">
            <li className="border-b-2 border-brand-yellow px-1 py-3.5 text-base">Une réponse de qualification sous 2 jours ouvrés</li>
            <li className="border-b-2 border-brand-yellow px-1 py-3.5 text-base">Un statut clair à chaque étape, notifié par email.</li>
            <li className="px-1 py-3.5 text-base">La roadmap et les livraisons des produits sur lesquels vous travaillez.</li>
          </ul>
        </div>
        <p className="relative m-0 text-[13px] text-muted-foreground">Réservé aux collaborateurs du groupe Europa</p>
      </section>

      <section
        aria-label="Connexion"
        className="bg-login-panel relative box-border flex min-w-0 flex-[1_1_480px] flex-col items-center justify-center gap-5 px-8 pb-14 pt-24"
      >
        <div className="box-border flex w-full max-w-[480px] flex-col gap-[22px] rounded-tag bg-login-surface px-12 pb-11 pt-12 shadow-lift">
          <div className="flex flex-col items-center gap-[22px]">
            <img src="/product-portal-logo.png" alt="Product Portal — Europa Group" className="block h-auto w-[150px]" />
            <h2 className="m-0 text-center text-[30px] font-bold leading-tight text-login-title">Bienvenue sur le Product Portal</h2>
          </div>
          <div aria-hidden="true" className="bg-brand-gradient-rule h-[3px] rounded-sm" />
          <p className="m-0 text-center text-[15px] leading-normal text-foreground-secondary">
            Connectez-vous avec votre compte Europa Group. Aucun mot de passe propre au portail.
          </p>
          {error && (
            <p role="alert" className="m-0 rounded-control border border-warning-border bg-warning px-3 py-2 text-[13px] text-warning-foreground">
              {ERRORS[error] ?? ERRORS.provider_error}
            </p>
          )}
          <a
            href={`/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`}
            className="flex min-h-[52px] items-center justify-center gap-2.5 rounded-tag bg-primary text-base font-bold text-primary-foreground no-underline hover:bg-primary-hover hover:text-primary-foreground"
          >
            <Lock aria-hidden="true" className="size-[18px]" />
            Connexion SSO
          </a>
          <p className="m-0 text-center text-[13px] text-muted-foreground">Un souci d'accès ? Contactez le support informatique.</p>
        </div>
        {providers.data?.dev && (
          <Link to="/connexion/dev" className="text-[13px]">
            Connexion de développement (NODE_ENV=development)
          </Link>
        )}
      </section>
    </div>
  );
}
